import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Ticket, TicketMessage, TicketMessageSource, TicketWithMessages, TicketAttachment,
  TicketStatus, TicketPriority, RoleType, MachineType, MachineDocumentation, AnalyzeTicketRequest,
} from '../types';
import { useSearchParams } from 'react-router-dom';
import { ticketApi, TicketStatusFilter, TicketPriorityFilter } from '../api/tickets';
import { machineTypeApi, documentationApi } from '../api/machines';
import { useAuth } from '../context/AuthContext';
import { MessageEditor, Markdown } from './RichText';
import TicketTimeline from './TicketTimeline';
import TicketTags from './TicketTags';
import Modal from './Modal';
import { Toast, useToast } from './Toast';
import { TICKET_STATUS_ORDER, ticketStatusMeta } from '../utils/ticketStatus';
import { attachmentDownloadUrl, documentationDownloadUrl } from '../utils/fileUrl';
import { useIsMobile } from '../hooks/useIsMobile';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const creatorTypeBadge = (role: RoleType | null): { className: string; label: string } | null => {
  if (!role) return null;
  if (role === 'CUSTOMER') return { className: 'badge badge-customer', label: 'Klient' };
  return { className: 'badge badge-integrator', label: 'Integrator' };
};

const formatTicketId = (n: number | null | undefined) => (n == null ? '#—' : `#${n}`);

const statusInfo = ticketStatusMeta;

const priorityInfo = (p: TicketPriority): { label: string; bg: string; color: string; border: string } => {
  if (p === 'CRITICAL') return { label: 'Krytyczny', bg: '#f7dedc', color: '#a52a23', border: '#eec3bf' };
  if (p === 'HIGH') return { label: 'Wysoki', bg: '#fbf2dd', color: '#7a5200', border: '#f6dca0' };
  if (p === 'LOW') return { label: 'Niski', bg: '#f1f2f4', color: '#5a636e', border: '#d7dade' };
  return { label: 'Normalny', bg: '#e7eaee', color: '#424b56', border: '#cfd5dc' };
};

export const PriorityBadge: React.FC<{ priority: TicketPriority }> = ({ priority }) => {
  const i = priorityInfo(priority);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
      padding: '0.1rem 0.5rem', fontSize: '0.7rem', fontWeight: 700,
      color: i.color, background: i.bg, border: `1px solid ${i.border}`,
      borderRadius: 999, letterSpacing: '0.02em',
    }}>
      {priority === 'CRITICAL' && '🔥 '}
      {i.label}
    </span>
  );
};

const isImage = (att: TicketAttachment) =>
  (att.contentType || '').startsWith('image/');

type Props = {
  machineId: string;
};

const TicketsTab: React.FC<Props> = ({ machineId }) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const isCustomer = user?.roles?.includes('CUSTOMER');
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selected, setSelected] = useState<TicketWithMessages | null>(null);
  const [loading, setLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const { toast, showToast, dismissToast } = useToast();
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set());
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [targetStatus, setTargetStatus] = useState<TicketStatus>('IN_PROGRESS');
  const [applyingStatus, setApplyingStatus] = useState(false);
  const [filter, setFilter] = useState<TicketStatusFilter>('open');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriorityFilter>('all');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const loadList = useCallback(async (p = 0) => {
    const { data } = await ticketApi.listByMachine(machineId, { page: p, filter, priority: priorityFilter });
    // Odporność na przejściowy stan (stary backend zwracał tablicę bez `content`).
    const raw = (Array.isArray(data) ? (data as unknown as Ticket[]) : data?.content) ?? [];
    // Filtry + sortowanie też po stronie klienta — idempotentne, gdy backend już to robi
    // (wtedy no-op), a działają nawet zanim backend zacznie honorować parametry.
    let list =
      filter === 'all' ? raw
        : filter === 'closed' ? raw.filter((t) => t.status === 'CLOSED')
          : raw.filter((t) => t.status !== 'CLOSED');
    if (priorityFilter !== 'all') list = list.filter((t) => t.priority === priorityFilter);
    const rank: Record<TicketPriority, number> = { CRITICAL: 0, HIGH: 1, NORMAL: 2, LOW: 3 };
    list = [...list].sort((a, b) =>
      rank[a.priority] - rank[b.priority]
      || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    setTickets(list);
    setTotalPages(data?.totalPages ?? 1);
    setPage(data?.page ?? 0);
    setSelectedTicketIds(new Set());
  }, [machineId, filter, priorityFilter]);

  const toggleTicket = (id: string) => setSelectedTicketIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const applyBulkStatus = async () => {
    const ids = Array.from(selectedTicketIds);
    if (ids.length === 0) return;
    setApplyingStatus(true);
    try {
      await Promise.all(ids.map((id) => ticketApi.setStatus(id, targetStatus)));
      await loadList(page);
      setShowStatusModal(false);
      showToast('success', `Zmieniono status: ${ids.length} zgł. → ${ticketStatusMeta(targetStatus).label}.`);
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się zmienić statusu.');
    } finally {
      setApplyingStatus(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openTicket = useCallback(async (id: string) => {
    const { data } = await ticketApi.get(id);
    setSelected(data);
  }, []);

  // Auto-open ticket if URL has ?ticketId=... (deep link from email)
  useEffect(() => {
    const deepLinkId = searchParams.get('ticketId');
    if (deepLinkId && (!selected || selected.ticket.id !== deepLinkId)) {
      openTicket(deepLinkId).catch(() => { /* ticket unavailable, ignore */ });
    }
  }, [searchParams, selected, openTicket]);

  if (selected) {
    return (
      <>
        <TicketDetail
          data={selected}
          onBack={async () => {
            setSelected(null);
            if (searchParams.has('ticketId')) {
              const sp = new URLSearchParams(searchParams);
              sp.delete('ticketId');
              setSearchParams(sp, { replace: true });
            }
            await loadList(page);
          }}
          onUpdate={(d) => setSelected(d)}
          loading={loading}
          setLoading={setLoading}
        />
        <Toast toast={toast} onDismiss={dismissToast} />
      </>
    );
  }

  return (
    <div>
      {isCustomer && (
        <div className="tab-action-bar" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowNewForm(true)}>
            Dodaj zgłoszenie
          </button>
        </div>
      )}
      {showNewForm && (
        <Modal title="Nowe zgłoszenie" maxWidth={640} onClose={() => setShowNewForm(false)}>
          <NewTicketForm
            machineId={machineId}
            onCreated={async (d) => {
              setShowNewForm(false);
              await loadList();
              setSelected(d);
              showToast('success', 'Zgłoszenie utworzone.');
            }}
            onError={(msg) => showToast('error', msg)}
            onCancel={() => setShowNewForm(false)}
          />
        </Modal>
      )}
      {/* Filtry — zawsze widoczne (żeby dało się wrócić z pustego filtra). Na mobile wyśrodkowane. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem',
        flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label className="form-label" style={{ margin: 0 }}>Status:</label>
          <select
            className="form-select form-select-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value as TicketStatusFilter)}
          >
            <option value="open">Otwarte</option>
            <option value="closed">Zamknięte</option>
            <option value="all">Wszystkie</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label className="form-label" style={{ margin: 0 }}>Priorytet:</label>
          <select
            className="form-select form-select-sm"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as TicketPriorityFilter)}
          >
            <option value="all">Wszystkie</option>
            <option value="CRITICAL">Krytyczny</option>
            <option value="HIGH">Wysoki</option>
            <option value="NORMAL">Normalny</option>
            <option value="LOW">Niski</option>
          </select>
        </div>
      </div>

      {tickets.length === 0 ? (
        <div className="empty-state">
          {priorityFilter !== 'all'
            ? 'Brak zgłoszeń dla wybranych filtrów.'
            : filter === 'open'
              ? 'Brak otwartych zgłoszeń.'
              : filter === 'closed'
                ? 'Brak zamkniętych zgłoszeń.'
                : isCustomer
                  ? 'Brak zgłoszeń — kliknij „Dodaj zgłoszenie", aby utworzyć pierwsze.'
                  : 'Brak zgłoszeń od klienta dla tej maszyny.'}
        </div>
      ) : (
        <>
          {!isCustomer && (
            <div style={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', marginBottom: '0.75rem' }}>
              <button className="btn btn-primary" disabled={selectedTicketIds.size === 0}
                onClick={() => setShowStatusModal(true)}>
                Zmień status{selectedTicketIds.size > 0 ? ` (${selectedTicketIds.size})` : ''}
              </button>
            </div>
          )}
          <div className="items-list">
          {tickets.map((t) => {
            const creator = creatorTypeBadge(t.createdByRole);
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                {!isCustomer && (
                  <input type="checkbox" checked={selectedTicketIds.has(t.id)}
                    onChange={() => toggleTicket(t.id)}
                    style={{ width: 16, height: 16, flexShrink: 0 }} />
                )}
                <div className="item-card card list-item-clickable" style={{ flex: 1, minWidth: 0 }} onClick={() => openTicket(t.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>
                        {formatTicketId(t.ticketNumber)}
                      </span>
                      <span style={{ fontWeight: 600 }}>{t.title}</span>
                      <PriorityBadge priority={t.priority} />
                      {t.assignedToFirstName && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                          → {t.assignedToFirstName} {t.assignedToLastName}
                        </span>
                      )}
                    </div>
                    <div className="item-meta" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span>{new Date(t.createdAt).toLocaleString('pl-PL')}</span>
                      {t.createdByFirstName && (
                        <>
                          <span>·</span>
                          <span>{t.createdByFirstName} {t.createdByLastName}</span>
                        </>
                      )}
                      {!t.createdByFirstName && t.reporterEmail && (
                        <>
                          <span>·</span>
                          <span>{t.reporterEmail}</span>
                        </>
                      )}
                      {creator && <span className={creator.className}>{creator.label}</span>}
                      {!creator && t.reporterEmail && <span className="badge badge-customer">Klient (gość)</span>}
                    </div>
                  </div>
                  <span className={statusInfo(t.status).badgeClass}>{statusInfo(t.status).label}</span>
                </div>
                </div>
              </div>
            );
          })}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <button className="btn btn-secondary btn-sm" disabled={page <= 0} onClick={() => loadList(page - 1)}>
                Poprzednia
              </button>
              <span className="item-meta pagination-info">Strona {page + 1} z {totalPages}</span>
              <button className="btn btn-secondary btn-sm" disabled={page >= totalPages - 1} onClick={() => loadList(page + 1)}>
                Następna
              </button>
            </div>
          )}
        </>
      )}

      {showStatusModal && (
        <Modal title="Zmień status" maxWidth={420} dismissable={!applyingStatus}
          onClose={() => setShowStatusModal(false)}>
          <p style={{ marginBottom: '0.75rem' }}>
            Ustaw status dla <strong>{selectedTicketIds.size}</strong> zaznaczonych zgłoszeń:
          </p>
          <div className="form-group">
            <select className="form-select" value={targetStatus}
              onChange={(e) => setTargetStatus(e.target.value as TicketStatus)}>
              {TICKET_STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{ticketStatusMeta(s).label}</option>
              ))}
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" disabled={applyingStatus}
              onClick={() => setShowStatusModal(false)}>
              Anuluj
            </button>
            <button type="button" className="btn btn-primary" disabled={applyingStatus}
              onClick={applyBulkStatus}>
              {applyingStatus ? 'Zapisuję…' : 'Zmień status'}
            </button>
          </div>
        </Modal>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

const NewTicketForm: React.FC<{
  machineId: string;
  onCreated: (data: TicketWithMessages) => void;
  onError?: (msg: string) => void;
  onCancel?: () => void;
}> = ({ machineId, onCreated, onError, onCancel }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [askAi, setAskAi] = useState(true);
  const [priority, setPriority] = useState<TicketPriority>('NORMAL');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files || []);
    const tooBig = list.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      setError(`Plik "${tooBig.name}" przekracza 10 MB`);
      e.target.value = '';
      return;
    }
    setError('');
    setFiles(list);
  };

  const submit = async () => {
    if (!title.trim() || !message.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await ticketApi.create(machineId, {
        title: title.trim(),
        firstMessage: message.trim(),
        askAi,
        priority,
      });
      for (const file of files) {
        try {
          await ticketApi.uploadAttachment(data.ticket.id, file);
        } catch (err: any) {
          console.error('Attachment upload failed', err);
        }
      }
      const refreshed = files.length > 0 ? await ticketApi.get(data.ticket.id) : { data };
      setTitle('');
      setMessage('');
      setAskAi(true);
      setFiles([]);
      onCreated(refreshed.data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Nie udało się utworzyć zgłoszenia';
      setError(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = title.trim().length > 0 && message.trim().length > 0;

  return (
    <div>
      {error && <div className="auth-error">{error}</div>}
      {/* Nad tytułem: checkbox AI po lewej, dropdown priorytetu po prawej. */}
      <div className="form-group" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '0.75rem', flexWrap: 'nowrap', marginBottom: '1.75rem',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, flex: '0 1 auto', minWidth: 0 }}>
          <input type="checkbox" checked={askAi} onChange={(e) => setAskAi(e.target.checked)} />
          Podpowiedź AI
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem', flex: '0 0 auto' }}>
          <label className="form-label" style={{ margin: 0 }}>Priorytet:</label>
          <select
            className="form-select form-select-sm"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
          >
            <option value="LOW">Niski</option>
            <option value="NORMAL">Normalny</option>
            <option value="HIGH">Wysoki</option>
            <option value="CRITICAL">Krytyczny — linia stoi</option>
          </select>
        </div>
      </div>
      <div className="form-group">
        <input
          className="form-input"
          placeholder="Tytuł zgłoszenia"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="form-group">
        <MessageEditor
          value={message}
          onChange={setMessage}
          placeholder="Opisz problem lub zadaj pytanie"
          rows={4}
        />
      </div>
      <div className="form-group">
        <label className="file-label">Załączniki (max 10 MB każdy)</label>
        <input type="file" multiple className="form-input" onChange={onFileChange} />
        {files.length > 0 && (
          <div className="item-meta" style={{ marginTop: '0.375rem' }}>
            Wybrano: {files.map((f) => f.name).join(', ')}
          </div>
        )}
      </div>
      <div className="modal-actions">
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Anuluj
          </button>
        )}
        <button
          className="btn btn-primary"
          onClick={submit}
          disabled={submitting || !canSubmit}
        >
          {submitting ? 'Wysyłanie...' : 'Utwórz zgłoszenie'}
        </button>
      </div>
    </div>
  );
};

const TicketDetail: React.FC<{
  data: TicketWithMessages;
  onBack: () => void;
  onUpdate: (d: TicketWithMessages) => void;
  loading: boolean;
  setLoading: (b: boolean) => void;
}> = ({ data, onBack, onUpdate, loading, setLoading }) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const isCustomer = user?.roles?.includes('CUSTOMER');
  // Funkcje AI w planach PRO i FREE (demo) — backend i tak to egzekwuje, tu chowamy przycisk.
  const hasAi = user?.plan === 'PRO' || user?.plan === 'FREE';
  const [message, setMessage] = useState('');
  const [askAi, setAskAi] = useState(true);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replyError, setReplyError] = useState('');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineKey, setTimelineKey] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data: fresh } = await ticketApi.get(data.ticket.id);
      onUpdate(fresh);
    } catch { /* ignore transient errors while polling */ }
  }, [data.ticket.id, onUpdate]);

  // While an AI answer is still generating, poll every 3s until it lands.
  const hasPending = data.messages.some((m) => m.pending);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(async () => {
      try {
        const { data: fresh } = await ticketApi.get(data.ticket.id);
        onUpdateRef.current(fresh);
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(t);
  }, [hasPending, data.ticket.id]);

  const send = async () => {
    if (!message.trim()) return;
    setLoading(true);
    setReplyError('');
    try {
      const { data: updated } = await ticketApi.addMessage(data.ticket.id, {
        content: message.trim(),
        askAi: !!isCustomer && askAi,
      });
      for (const file of replyFiles) {
        try {
          await ticketApi.uploadAttachment(data.ticket.id, file);
        } catch (err) {
          console.error('Attachment upload failed', err);
        }
      }
      const refreshed = replyFiles.length > 0 ? await ticketApi.get(data.ticket.id) : { data: updated };
      setMessage('');
      setReplyFiles([]);
      onUpdate(refreshed.data);
    } finally {
      setLoading(false);
    }
  };

  const onReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files || []);
    const tooBig = list.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      setReplyError(`Plik "${tooBig.name}" przekracza 10 MB`);
      e.target.value = '';
      return;
    }
    setReplyError('');
    setReplyFiles(list);
  };

  const setStatus = async (next: TicketStatus) => {
    await ticketApi.setStatus(data.ticket.id, next);
    const { data: fresh } = await ticketApi.get(data.ticket.id);
    onUpdate(fresh);
  };

  const REOPEN_WINDOW_DAYS = 14;
  const daysSinceClosed = data.ticket.closedAt
    ? (Date.now() - new Date(data.ticket.closedAt).getTime()) / 86_400_000
    : null;
  // Klient: do 14 dni od zamknięcia (brak closedAt = stare zgłoszenie sprzed migracji — pozwalamy).
  // Staff: bez limitu. Backend i tak egzekwuje okno niezależnie od UI.
  const canReopen = data.ticket.status === 'CLOSED'
    && (!isCustomer || daysSinceClosed === null || daysSinceClosed <= REOPEN_WINDOW_DAYS);

  const reopen = async () => {
    setReplyError('');
    try {
      await ticketApi.reopen(data.ticket.id);
      const { data: fresh } = await ticketApi.get(data.ticket.id);
      onUpdate(fresh);
    } catch (e: any) {
      setReplyError(e?.response?.data?.message || 'Nie udało się ponownie otworzyć zgłoszenia.');
    }
  };

  const setPriorityOnTicket = async (next: TicketPriority) => {
    await ticketApi.setPriority(data.ticket.id, next);
    const { data: fresh } = await ticketApi.get(data.ticket.id);
    onUpdate(fresh);
  };

  const deleteAttachment = async (attId: string) => {
    if (!window.confirm('Usunąć załącznik?')) return;
    await ticketApi.deleteAttachment(data.ticket.id, attId);
    const { data: fresh } = await ticketApi.get(data.ticket.id);
    onUpdate(fresh);
  };

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: '1rem' }}>
        &larr; Powrót do listy
      </button>

      <div className="card" style={{ marginBottom: '1rem', padding: isMobile ? '0.875rem' : '1.25rem', textAlign: isMobile ? 'center' : undefined }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: isMobile ? '0.6875rem' : '0.75rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>
            {formatTicketId(data.ticket.ticketNumber)}
          </span>
          {/* Badge statusu w prawym górnym rogu karty. */}
          <span className={statusInfo(data.ticket.status).badgeClass}>
            {statusInfo(data.ticket.status).label}
          </span>
        </div>
        {isMobile ? (
          <>
            {/* 1: tytuł, 2: priorytet, 3: data, 4: kto zgłosił — wszystko wyśrodkowane */}
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.01em' }}>{data.ticket.title}</h2>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
              <PriorityBadge priority={data.ticket.priority} />
            </div>
            <div className="item-meta" style={{ marginBottom: '0.25rem' }}>
              {new Date(data.ticket.createdAt).toLocaleString('pl-PL')}
            </div>
            <div className="item-meta" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {data.ticket.createdByFirstName && (
                <span>{data.ticket.createdByFirstName} {data.ticket.createdByLastName}</span>
              )}
              {!data.ticket.createdByFirstName && data.ticket.reporterEmail && (
                <span>{data.ticket.reporterEmail}</span>
              )}
              {(() => {
                const c = creatorTypeBadge(data.ticket.createdByRole);
                if (c) return <span className={c.className}>{c.label}</span>;
                return data.ticket.reporterEmail ? <span className="badge badge-customer">Klient (gość)</span> : null;
              })()}
            </div>
            {data.ticket.assignedToFirstName && (
              <div className="item-meta" style={{ marginTop: '0.25rem' }}>
                Przypisane do: <strong>{data.ticket.assignedToFirstName} {data.ticket.assignedToLastName}</strong>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.01em' }}>{data.ticket.title}</h2>
              <PriorityBadge priority={data.ticket.priority} />
            </div>
            <div className="item-meta" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              <span>{new Date(data.ticket.createdAt).toLocaleString('pl-PL')}</span>
              {data.ticket.createdByFirstName && (
                <>
                  <span>·</span>
                  <span>{data.ticket.createdByFirstName} {data.ticket.createdByLastName}</span>
                </>
              )}
              {!data.ticket.createdByFirstName && data.ticket.reporterEmail && (
                <>
                  <span>·</span>
                  <span>{data.ticket.reporterEmail}</span>
                </>
              )}
              {(() => {
                const c = creatorTypeBadge(data.ticket.createdByRole);
                if (c) return <span className={c.className}>{c.label}</span>;
                return data.ticket.reporterEmail ? <span className="badge badge-customer">Klient (gość)</span> : null;
              })()}
              {data.ticket.assignedToFirstName && (
                <>
                  <span>·</span>
                  <span style={{ fontSize: '0.75rem' }}>
                    Przypisane do: <strong>{data.ticket.assignedToFirstName} {data.ticket.assignedToLastName}</strong>
                  </span>
                </>
              )}
            </div>
          </>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'space-between',
          gap: '0.75rem',
          flexWrap: 'wrap',
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--color-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'center' : undefined, gap: '0.5rem', flexWrap: 'wrap' }}>
            {canReopen && (
              <button
                className="btn btn-primary btn-sm"
                onClick={reopen}
                title={isCustomer
                  ? `Możliwe do ${REOPEN_WINDOW_DAYS} dni od zamknięcia`
                  : 'Otwiera zgłoszenie ponownie'}
              >
                Otwórz ponownie
              </button>
            )}
            {isCustomer && data.ticket.status === 'CLOSED' && !canReopen && (
              <span className="item-meta">
                Od zamknięcia minęło ponad {REOPEN_WINDOW_DAYS} dni — utwórz nowe zgłoszenie.
              </span>
            )}
            {!isCustomer && (
              <select
                className="form-select form-select-sm"
                value=""
                onChange={(e) => {
                  const next = e.target.value as TicketStatus;
                  if (next && next !== data.ticket.status) setStatus(next);
                  e.target.value = '';
                }}
                style={{ minWidth: 180 }}
              >
                <option value="">Zmień status…</option>
                {TICKET_STATUS_ORDER.filter((s) => s !== data.ticket.status).map((s) => (
                  <option key={s} value={s}>{ticketStatusMeta(s).label}</option>
                ))}
              </select>
            )}
            {!isCustomer && (
              <select
                className="form-select form-select-sm"
                value={data.ticket.priority}
                onChange={(e) => setPriorityOnTicket(e.target.value as TicketPriority)}
                style={{ minWidth: 150 }}
                title="Zmień priorytet"
              >
                <option value="LOW">Priorytet: Niski</option>
                <option value="NORMAL">Priorytet: Normalny</option>
                <option value="HIGH">Priorytet: Wysoki</option>
                <option value="CRITICAL">Priorytet: Krytyczny</option>
              </select>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'center' : undefined, gap: '0.5rem', flexWrap: 'wrap' }}>
            {!isCustomer && hasAi && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowAnalysisModal(true)}>
                ✦ Analiza AI
              </button>
            )}
            <TicketTags ticketId={data.ticket.id} onChange={() => setTimelineKey((k) => k + 1)} />
          </div>
        </div>
      </div>

      {data.attachments && data.attachments.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem', padding: isMobile ? '0.75rem 0.875rem' : '1rem 1.25rem' }}>
          <div className="eyebrow" style={{ marginBottom: '0.625rem' }}>Załączniki ({data.attachments.length})</div>
          <div style={{ display: 'flex', gap: isMobile ? '0.5rem' : '0.75rem', flexWrap: 'wrap' }}>
            {data.attachments.map((att) => (
              <div key={att.id} style={{
                border: '1px solid var(--color-border-strong)',
                borderRadius: 'var(--radius)',
                padding: '0.5rem',
                background: 'var(--color-surface-alt)',
                minWidth: isMobile ? 96 : 140,
                maxWidth: isMobile ? 130 : 200,
                flex: isMobile ? '1 1 96px' : '0 0 auto',
              }}>
                {isImage(att) ? (
                  <a href={attachmentDownloadUrl(data.ticket.id, att.id)} target="_blank" rel="noopener noreferrer">
                    <img src={attachmentDownloadUrl(data.ticket.id, att.id)} alt={att.originalName || 'załącznik'}
                      style={{ width: '100%', height: isMobile ? 72 : 100, objectFit: 'cover', borderRadius: 4 }} />
                  </a>
                ) : (
                  <a href={attachmentDownloadUrl(data.ticket.id, att.id)} target="_blank" rel="noopener noreferrer"
                    style={{ height: isMobile ? 72 : 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-surface)', borderRadius: 4, fontSize: isMobile ? '1.5rem' : '2rem' }}>
                    📄
                  </a>
                )}
                <div style={{ fontSize: isMobile ? '0.6875rem' : '0.75rem', marginTop: '0.375rem', wordBreak: 'break-all' }}>
                  {att.originalName || `att-${att.id}`}
                </div>
                {att.sizeBytes != null && (
                  <div className="item-meta">{(att.sizeBytes / 1024).toFixed(0)} KB</div>
                )}
                <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.375rem', width: '100%' }}
                  onClick={() => deleteAttachment(att.id)}>
                  Usuń
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="items-list" style={{ marginBottom: '1rem' }}>
        {data.messages.map((m) => (
          <MessageBubble key={m.id} message={m} ticketId={data.ticket.id} onUpdate={onUpdate} onRefresh={refresh} />
        ))}
        {loading && <div className="empty-state">AI przygotowuje odpowiedź...</div>}
      </div>

      <div className="add-form card">
        {replyError && <div className="auth-error">{replyError}</div>}
        <MessageEditor
          value={message}
          onChange={setMessage}
          placeholder="Twoja wiadomość..."
          rows={4}
        />
        <div className="form-group" style={{ marginTop: '0.5rem' }}>
          <label className="file-label">Załączniki (max 10 MB każdy)</label>
          <input type="file" multiple className="form-input" onChange={onReplyFileChange} />
          {replyFiles.length > 0 && (
            <div className="item-meta" style={{ marginTop: '0.375rem' }}>
              Wybrano: {replyFiles.map((f) => f.name).join(', ')}
            </div>
          )}
        </div>
        {isCustomer && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0' }}>
            <input type="checkbox" checked={askAi} onChange={(e) => setAskAi(e.target.checked)} />
            Zapytaj AI
          </label>
        )}
        <button className="btn btn-primary btn-sm" onClick={send} disabled={loading || !message.trim()} style={{ marginTop: isCustomer ? 0 : '0.5rem' }}>
          {loading ? 'Wysyłanie...' : 'Wyślij'}
        </button>
      </div>

      <div className="card" style={{ marginTop: '1rem', padding: '1rem' }}>
        <button
          type="button"
          onClick={() => { setShowTimeline((v) => !v); setTimelineKey((k) => k + 1); }}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: 'var(--color-text)',
          }}
        >
          <span style={{ transform: showTimeline ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▶</span>
          Historia zgłoszenia
        </button>
        {showTimeline && (
          <div style={{ marginTop: '0.75rem' }}>
            <TicketTimeline ticketId={data.ticket.id} refreshKey={timelineKey} />
          </div>
        )}
      </div>

      {showAnalysisModal && (
        <AnalysisModal
          ticket={data}
          onClose={() => setShowAnalysisModal(false)}
          onAnalyzed={(updated) => onUpdate(updated)}
        />
      )}
    </div>
  );
};

type BubbleStyle = {
  bg: string;
  border: string;
  label: string;
  labelColor: string;
  marginLeft: number;
};

const customerStyle: BubbleStyle = {
  bg: '#fdf2f8',
  border: '#be185d',
  label: 'Klient',
  labelColor: '#be185d',
  marginLeft: 0,
};
const integratorStyle: BubbleStyle = {
  bg: '#ecfdf5',
  border: '#059669',
  label: 'Integrator',
  labelColor: '#047857',
  marginLeft: 24,
};
const aiStyle: BubbleStyle = {
  bg: '#e2eefb',
  border: '#b9d6f5',
  label: 'AI Asystent',
  labelColor: '#17538f',
  marginLeft: 48,
};
const fallbackStyle: BubbleStyle = {
  bg: 'var(--color-surface)',
  border: 'var(--color-border-strong)',
  label: 'Użytkownik',
  labelColor: 'var(--color-text-secondary)',
  marginLeft: 0,
};

const resolveBubbleStyle = (message: TicketMessage): BubbleStyle => {
  if (message.role === 'AI') return aiStyle;
  if (message.authorRole === 'CUSTOMER') return customerStyle;
  if (message.authorRole === 'ADMIN' || message.authorRole === 'EMPLOYEE_ADMIN' || message.authorRole === 'EMPLOYEE') return integratorStyle;
  return fallbackStyle;
};

const MessageBubble: React.FC<{
  message: TicketMessage;
  ticketId: string;
  onUpdate: (d: TicketWithMessages) => void;
  onRefresh?: () => void;
}> = ({ message, ticketId, onUpdate, onRefresh }) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const isStaff = !!(user?.roles?.includes('ADMIN') || user?.roles?.includes('EMPLOYEE_ADMIN') || user?.roles?.includes('EMPLOYEE'));
  const isCustomer = !!user?.roles?.includes('CUSTOMER');
  const [verifying, setVerifying] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);
  const style = resolveBubbleStyle(message);
  // Na mobile spłaszczamy wcięcia wątku (0/24/48 → 0/12/24), tniemy padding i skalujemy
  // typografię całego dymka — treść (Markdown) dziedziczy font-size z kontenera.
  const bubbleMargin = isMobile ? Math.round(style.marginLeft / 2) : style.marginLeft;
  const bubblePadding = isMobile ? '0.75rem 0.875rem' : '1rem 1.25rem';
  const bubbleFontSize = isMobile ? '0.8125rem' : undefined;
  const sources: TicketMessageSource[] = message.sourcesJson ? safeParse(message.sourcesJson) : [];
  const author = message.authorFirstName ? `${message.authorFirstName} ${message.authorLastName}` : null;
  const avatarInitials = message.authorFirstName
    ? `${message.authorFirstName[0] || ''}${message.authorLastName?.[0] || ''}`
    : null;
  const isAi = message.role === 'AI';
  const isVerified = !!message.verifiedAt;
  const verifier = message.verifiedByFirstName
    ? `${message.verifiedByFirstName} ${message.verifiedByLastName || ''}`.trim()
    : 'integratora';

  if (message.pending) {
    return (
      <div className="card" style={{
        background: style.bg, borderLeft: `4px solid ${style.border}`,
        marginLeft: bubbleMargin, padding: bubblePadding, fontSize: bubbleFontSize,
      }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <span className="eyebrow" style={{ color: style.labelColor, fontWeight: 700 }}>{style.label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', color: 'var(--color-text-secondary)' }}>
          <span className="ai-spinner" aria-hidden="true" />
          <span>Generuję odpowiedź na podstawie dokumentacji… To może chwilę potrwać.</span>
          {onRefresh && (
            <button type="button" className="btn btn-primary btn-sm" onClick={onRefresh}>
              Odśwież
            </button>
          )}
        </div>
      </div>
    );
  }

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const { data } = await ticketApi.verifyMessage(ticketId, message.id);
      onUpdate(data);
    } catch (e) {
      console.error('Verify failed', e);
    } finally {
      setVerifying(false);
    }
  };

  const handleFeedback = async (helpful: boolean) => {
    setSavingFeedback(true);
    try {
      const { data } = await ticketApi.setMessageFeedback(ticketId, message.id, helpful);
      onUpdate(data);
    } catch (e) {
      console.error('Feedback failed', e);
    } finally {
      setSavingFeedback(false);
    }
  };

  return (
    <div
      className="card"
      style={{
        background: style.bg,
        borderLeft: `4px solid ${style.border}`,
        marginLeft: bubbleMargin,
        padding: bubblePadding,
        fontSize: bubbleFontSize,
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
        marginBottom: '0.5rem',
      }}>
        <span className="eyebrow" style={{ color: style.labelColor, fontWeight: 700 }}>
          {style.label}
        </span>
        {author && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
            <span style={{ color: 'var(--color-text-muted)' }}>·</span>
            {message.authorAvatarUrl ? (
              <img src={message.authorAvatarUrl} alt={author}
                style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
            ) : avatarInitials ? (
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: 'rgba(0,0,0,0.06)', color: 'var(--color-text-secondary)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.65rem', fontWeight: 600,
              }}>{avatarInitials}</span>
            ) : null}
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontWeight: 500 }}>{author}</span>
          </span>
        )}
        {isAi && (
          isVerified ? (
            <span title={`Zweryfikowane przez ${verifier} • ${new Date(message.verifiedAt!).toLocaleString('pl-PL')}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.125rem 0.5rem',
                fontSize: '0.7rem', fontWeight: 600,
                color: '#047857', background: '#d1fae5',
                border: '1px solid #6ee7b7',
                borderRadius: '999px',
                letterSpacing: '0.01em',
              }}>
              ✓ Zweryfikowane przez {verifier}
            </span>
          ) : (
            <span title="Odpowiedź wygenerowana przez AI — czeka na weryfikację integratora"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.125rem 0.5rem',
                fontSize: '0.7rem', fontWeight: 600,
                color: '#b45309', background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '999px',
                letterSpacing: '0.01em',
              }}>
              ⚠ Niezweryfikowane przez integratora
            </span>
          )
        )}
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
          {new Date(message.createdAt).toLocaleString('pl-PL')}
        </span>
      </div>
      <div>
        <Markdown content={stripFootnotes(message.content)} color={style.labelColor} borderColor={style.border} />
      </div>

      {sources.length > 0 && (() => {
        const usedRefs = extractRefs(message.content, sources.length);
        const refsToShow = usedRefs.length > 0 ? usedRefs : [1];

        type Group = {
          source: TicketMessageSource;
          refs: Array<{ n: number; page: number | null }>;
        };
        const groups: Group[] = [];
        const byDoc = new Map<string, Group>();
        for (const n of refsToShow) {
          const src = sources[n - 1];
          if (!src) continue;
          const existing = byDoc.get(src.documentationId);
          if (existing) {
            existing.refs.push({ n, page: src.page });
          } else {
            const g: Group = { source: src, refs: [{ n, page: src.page }] };
            byDoc.set(src.documentationId, g);
            groups.push(g);
          }
        }

        return (
          <div style={{
            marginTop: '1rem',
            paddingTop: '0.875rem',
            borderTop: `1px dashed ${style.border}`,
          }}>
            <div className="eyebrow" style={{ color: style.labelColor, fontWeight: 700, marginBottom: '0.625rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span aria-hidden="true">📚</span>
              Źródła
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {groups.map(({ source, refs }) => {
                const docTitle = source.documentationName || source.filename || `Dokument #${source.documentationId}`;
                return (
                  <li key={source.documentationId} style={{
                    display: 'flex',
                    alignItems: 'stretch',
                    background: '#ffffff',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    overflow: 'hidden',
                    fontSize: '0.8125rem',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  }}>
                    <span aria-hidden="true" style={{
                      width: 6,
                      flexShrink: 0,
                      background: `linear-gradient(180deg, ${style.border} 0%, ${style.labelColor} 100%)`,
                    }} />
                    <div style={{
                      flex: 1, minWidth: 0,
                      padding: '0.5rem 0.75rem',
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {docTitle}
                        </div>
                        <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem', marginTop: '0.125rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {refs.map((r) => (
                            <span key={r.n}>
                              <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', color: style.labelColor, fontWeight: 600 }}>[{r.n}]</span>
                              {r.page != null && <> str. {r.page}</>}
                            </span>
                          ))}
                        </div>
                      </div>
                      {source.documentationId && (
                        <a
                          href={documentationDownloadUrl(source.documentationId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm"
                          style={{ flexShrink: 0, textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                          title="Pobierz dokument"
                        >
                          ↓ Pobierz
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })()}

      {isAi && isCustomer && (
        <div style={{
          marginTop: '0.875rem',
          paddingTop: '0.75rem',
          borderTop: `1px dashed ${style.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}>
          {message.helpful == null ? (
            <>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                Czy ta informacja była pomocna?
              </span>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  type="button"
                  onClick={() => handleFeedback(true)}
                  disabled={savingFeedback}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    padding: '0.35rem 0.85rem',
                    fontSize: '0.8125rem', fontWeight: 600,
                    color: '#047857', background: '#d1fae5',
                    border: '1px solid #6ee7b7', borderRadius: 999,
                    cursor: savingFeedback ? 'wait' : 'pointer',
                  }}
                >
                  👍 TAK
                </button>
                <button
                  type="button"
                  onClick={() => handleFeedback(false)}
                  disabled={savingFeedback}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    padding: '0.35rem 0.85rem',
                    fontSize: '0.8125rem', fontWeight: 600,
                    color: '#b91c1c', background: '#fee2e2',
                    border: '1px solid #fca5a5', borderRadius: 999,
                    cursor: savingFeedback ? 'wait' : 'pointer',
                  }}
                >
                  👎 NIE
                </button>
              </div>
            </>
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            }}>
              <span>{message.helpful ? '👍' : '👎'}</span>
              <span>Dziękujemy za feedback — oznaczyłeś jako <strong>{message.helpful ? 'pomocne' : 'niepomocne'}</strong>.</span>
              <button
                type="button"
                onClick={() => handleFeedback(!message.helpful!)}
                disabled={savingFeedback}
                style={{
                  background: 'none', border: 'none',
                  color: style.labelColor, fontSize: '0.75rem',
                  cursor: 'pointer', textDecoration: 'underline',
                  padding: 0, marginLeft: '0.25rem',
                }}
              >
                zmień
              </button>
            </span>
          )}
        </div>
      )}

      {isAi && isStaff && message.helpful != null && (
        <div style={{
          marginTop: '0.5rem',
          fontSize: '0.75rem',
          color: 'var(--color-text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}>
          <span aria-hidden="true">{message.helpful ? '👍' : '👎'}</span>
          Klient oznaczył tę odpowiedź jako {message.helpful ? 'pomocną' : 'niepomocną'}
        </div>
      )}

      {isAi && isStaff && !isVerified && (
        <div style={{
          marginTop: '0.875rem',
          paddingTop: '0.75rem',
          borderTop: `1px dashed ${style.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button
            type="button"
            onClick={handleVerify}
            disabled={verifying}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.45rem 0.9rem',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: '#ffffff',
              background: verifying ? '#9ca3af' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              border: '1px solid #047857',
              borderRadius: '999px',
              cursor: verifying ? 'wait' : 'pointer',
              boxShadow: '0 1px 3px rgba(5, 150, 105, 0.25)',
              transition: 'transform 0.12s, box-shadow 0.18s, filter 0.18s',
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <span aria-hidden="true">✓</span>
            {verifying ? 'Potwierdzam...' : 'Potwierdź odpowiedź AI'}
          </button>
        </div>
      )}
    </div>
  );
};

function stripFootnotes(content: string): string {
  if (!content) return '';
  const idx = content.search(/\n\s*—\s*—\s*—\s*\n\s*\*\*Źródła:\*\*/);
  return idx >= 0 ? content.slice(0, idx).trimEnd() : content;
}

function extractRefs(content: string, max: number): number[] {
  if (!content) return [];
  const body = stripFootnotes(content);
  const seen = new Set<number>();
  const result: number[] = [];
  const re = /\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= max && !seen.has(n)) {
      seen.add(n);
      result.push(n);
    }
  }
  return result.sort((a, b) => a - b);
}

function safeParse(s: string): TicketMessageSource[] {
  try {
    const parsed = JSON.parse(s);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const AnalysisModal: React.FC<{
  ticket: TicketWithMessages;
  onClose: () => void;
  onAnalyzed: (updated: TicketWithMessages) => void;
}> = ({ ticket, onClose, onAnalyzed }) => {
  const [machineTypes, setMachineTypes] = useState<MachineType[]>([]);
  const [machineDocs, setMachineDocs] = useState<MachineDocumentation[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const [analyzeNotes, setAnalyzeNotes] = useState(false);
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([]);
  const [analyzeDocs, setAnalyzeDocs] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [analyzePastTickets, setAnalyzePastTickets] = useState(false);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [draftText, setDraftText] = useState<string | null>(null);
  const [sources, setSources] = useState<{ kind: string; label: string }[]>([]);
  const [includeSources, setIncludeSources] = useState(true);
  const [publishing, setPublishing] = useState(false);

  const ticketContent = ticket.messages
    .filter((m) => m.role === 'USER')
    .map((m) => m.content)
    .join('\n\n');
  const imageAttachmentCount = ticket.attachments.filter(isImage).length;

  useEffect(() => {
    Promise.all([
      machineTypeApi.getAll(),
      documentationApi.getByMachineId(ticket.ticket.machineId),
    ])
      .then(([typesRes, docsRes]) => {
        setMachineTypes(typesRes.data);
        setMachineDocs(docsRes.data);
      })
      .finally(() => setLoadingMeta(false));
  }, [ticket.ticket.machineId]);

  const toggleType = (id: string) => {
    setSelectedTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleDoc = (id: string) => {
    setSelectedDocIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const run = async () => {
    setRunning(true);
    setError('');
    try {
      const req: AnalyzeTicketRequest = {
        analyzeNotes,
        machineTypeIds: analyzeNotes ? selectedTypeIds : [],
        analyzeDocs,
        documentationIds: analyzeDocs ? selectedDocIds : [],
        analyzePastTickets,
      };
      const { data } = await ticketApi.analyze(ticket.ticket.id, req);
      setDraftText(data.text);
      setSources(data.sources || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Analiza nie powiodła się');
    } finally {
      setRunning(false);
    }
  };

  const publish = async () => {
    if (!draftText || !draftText.trim()) return;
    setPublishing(true);
    setError('');
    try {
      const sourcesBlock = includeSources && sources.length > 0
        ? `\n\n---\n**Źródła:**\n${sources.map((s) => `- ${s.kind}: ${s.label}`).join('\n')}`
        : '';
      const content = draftText.trim() + sourcesBlock;
      const { data } = await ticketApi.addMessage(ticket.ticket.id, { content, askAi: false });
      onAnalyzed(data);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się opublikować odpowiedzi');
    } finally {
      setPublishing(false);
    }
  };

  const canRun = !running && (
    (analyzeNotes && selectedTypeIds.length > 0) ||
    (analyzeDocs && selectedDocIds.length > 0) ||
    analyzePastTickets ||
    imageAttachmentCount > 0
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 920 }}>
        <h2 className="modal-title">Analiza AI — {formatTicketId(ticket.ticket.ticketNumber)}</h2>

        <div className="form-group">
          <label className="eyebrow" style={{ display: 'block', marginBottom: '0.375rem' }}>
            Treść zgłoszenia
          </label>
          <div style={{
            padding: '0.75rem',
            background: 'var(--color-surface-alt)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            fontSize: '0.875rem',
            maxHeight: 140,
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
          }}>
            <strong>{ticket.ticket.title}</strong>
            {ticketContent && <>{'\n\n'}{ticketContent}</>}
          </div>
          {imageAttachmentCount > 0 && (
            <div className="item-meta" style={{ marginTop: '0.375rem' }}>
              + {imageAttachmentCount} zrzut(y) ekranu zostaną przekazane do analizy.
            </div>
          )}
        </div>

        <div className="form-group" style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
            <input type="checkbox" checked={analyzeNotes} onChange={(e) => setAnalyzeNotes(e.target.checked)} />
            Analizuj notatki z innych maszyn
          </label>
          {analyzeNotes && (
            <div style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              {loadingMeta ? (
                <div className="item-meta">Ładowanie typów...</div>
              ) : machineTypes.length === 0 ? (
                <div className="item-meta">Brak typów maszyn</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {machineTypes.map((t) => (
                    <label key={t.id} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      padding: '0.25rem 0.625rem',
                      border: '1px solid var(--color-border-strong)',
                      borderRadius: 'var(--radius)',
                      fontSize: '0.8125rem',
                      cursor: 'pointer',
                      background: selectedTypeIds.includes(t.id) ? 'var(--color-primary-light)' : 'var(--color-surface)',
                    }}>
                      <input type="checkbox"
                        checked={selectedTypeIds.includes(t.id)}
                        onChange={() => toggleType(t.id)} />
                      {t.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-group" style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
            <input type="checkbox" checked={analyzePastTickets} onChange={(e) => setAnalyzePastTickets(e.target.checked)} />
            Analizuj poprzednie zgłoszenia
          </label>
          {analyzePastTickets && (
            <div className="item-meta" style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              AI przeszuka rozwiązane (zamknięte) zgłoszenia podobne tematycznie i wymieni ich numery w odpowiedzi.
            </div>
          )}
        </div>

        <div className="form-group" style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
            <input type="checkbox" checked={analyzeDocs} onChange={(e) => setAnalyzeDocs(e.target.checked)} />
            Analizuj dokumentację
          </label>
          {analyzeDocs && (
            <div style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              {loadingMeta ? (
                <div className="item-meta">Ładowanie dokumentów...</div>
              ) : machineDocs.length === 0 ? (
                <div className="item-meta">Brak dokumentacji podpiętej do tej maszyny</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {machineDocs.map((d) => (
                    <label key={d.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                    }}>
                      <input type="checkbox"
                        checked={selectedDocIds.includes(d.id)}
                        onChange={() => toggleDoc(d.id)} />
                      {d.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {error && <div className="auth-error">{error}</div>}

        {draftText !== null && (
          <div style={{ marginTop: '1rem', borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
            <label className="eyebrow" style={{ display: 'block', marginBottom: '0.375rem' }}>
              Wersja robocza — edytuj przed opublikowaniem
            </label>
            <MessageEditor
              value={draftText}
              onChange={setDraftText}
              placeholder="Treść odpowiedzi…"
              rows={12}
            />

            {sources.length > 0 && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem 1rem',
                background: 'var(--color-surface-alt)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
              }}>
                <div className="eyebrow" style={{ marginBottom: '0.5rem' }}>
                  Źródła użyte w analizie ({sources.length})
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {sources.map((s, i) => (
                    <li key={i} style={{ fontSize: '0.8125rem' }}>
                      <span style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{s.kind}:</span>{' '}
                      {s.label}
                    </li>
                  ))}
                </ul>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.6rem', fontSize: '0.8125rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={includeSources} onChange={(e) => setIncludeSources(e.target.checked)} />
                  Dołącz sekcję „Źródła" do publikowanej odpowiedzi (widoczną dla klienta)
                </label>
              </div>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={running || publishing}>
            Zamknij
          </button>
          <button className="btn btn-primary" onClick={run} disabled={!canRun || publishing}>
            {running ? 'Analizuję…' : draftText !== null ? '✦ Analizuj ponownie' : '✦ Analizuj'}
          </button>
          {draftText !== null && (
            <button className="btn btn-primary" onClick={publish} disabled={publishing || running || !draftText.trim()}>
              {publishing ? 'Publikuję…' : 'Opublikuj'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketsTab;
